/* ============================================================
   COREVIAN UNIVERSITY
   ADMIN DOCUMENT COMMAND CENTER
   Production Controlled-Document Administration
   ============================================================ */

(function () {
  'use strict';

  const CVN_ADMIN_DOCS = {
    supabaseUrl: 'https://xoolcbbpnvdylcoorlqg.supabase.co',
    publishableKey: 'sb_publishable_bjOrV5qp2n0PtuVFOSA_6g_3Micws2T'
  };

  let adminRegister = [];
  let adminPanelInstalled = false;

  /* ==========================================================
     AUTH
     ========================================================== */

async function getAuthSession() {
  try {
    if (typeof window.getCorevianSupabaseClient !== 'function') {
      console.error(
        'Corevian Admin Documents: University Supabase client is not available.'
      );
      return null;
    }

    const client = window.getCorevianSupabaseClient();

    if (!client) {
      return null;
    }

    const { data, error } = await client.auth.getSession();

    if (error) {
      throw error;
    }

    return data?.session || null;

  } catch (error) {
    console.error(
      'Corevian Admin Documents auth error:',
      error
    );
    return null;
  }
}

  /* ==========================================================
     RPC
     ========================================================== */

  async function callAdminRPC(functionName, payload = {}) {
    const session = await getAuthSession();

    if (!session || !session.access_token) {
      throw new Error('Authentication session not available.');
    }

    const response = await fetch(
      `${CVN_ADMIN_DOCS.supabaseUrl}/rest/v1/rpc/${functionName}`,
      {
        method: 'POST',
        headers: {
          apikey: CVN_ADMIN_DOCS.publishableKey,
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    const text = await response.text();

    let body;

    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }

    if (!response.ok) {
      const message =
        body?.message ||
        body?.error ||
        body?.hint ||
        text ||
        'Controlled-document request failed.';

      throw new Error(message);
    }

    return body;
  }

  async function loadAdminRegister() {
    return await callAdminRPC(
      'get_admin_controlled_document_register',
      {}
    );
  }

  /* ==========================================================
     HELPERS
     ========================================================== */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatDate(value) {
    if (!value) return '—';

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return escapeHTML(value);
      }

      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return escapeHTML(value);
    }
  }

  function statusLabel(status) {
    const map = {
      draft: 'Draft',
      under_review: 'Under Review',
      approved: 'Approved',
      retired: 'Retired',
      superseded: 'Superseded'
    };

    return map[status] || status || 'Unknown';
  }

  function statusClass(status) {
    const allowed = [
      'draft',
      'under_review',
      'approved',
      'retired',
      'superseded'
    ];

    return allowed.includes(status)
      ? `cvn-admin-status-${status}`
      : 'cvn-admin-status-default';
  }

  function isDemoMode() {
    const banner = document.getElementById('demoBanner');

    if (!banner) return false;

    return window.getComputedStyle(banner).display !== 'none';
  }

  /* ==========================================================
     STYLES
     ========================================================== */

  function installStyles() {
    if (document.getElementById('cvn-admin-doc-styles')) return;

    const style = document.createElement('style');
    style.id = 'cvn-admin-doc-styles';

    style.textContent = `
      #cvnAdminDocsLauncher {
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 9000;
        border: 1px solid rgba(25,181,176,.45);
        background: #0B1426;
        color: #ffffff;
        padding: 12px 18px;
        border-radius: 9px;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .7px;
        cursor: pointer;
        box-shadow: 0 12px 32px rgba(0,0,0,.28);
        font-family: inherit;
      }

      #cvnAdminDocsLauncher:hover {
        border-color: #19B5B0;
        transform: translateY(-1px);
      }

      #cvnAdminDocsOverlay {
        position: fixed;
        inset: 0;
        z-index: 12000;
        display: none;
        background: rgba(4,12,25,.86);
        backdrop-filter: blur(5px);
        overflow-y: auto;
        padding: 30px;
        box-sizing: border-box;
      }

      #cvnAdminDocsOverlay.cvn-open {
        display: block;
      }

      /* ==========================================================
         COREVIAN — CONTROLLED DOCUMENT GOVERNANCE WORKSPACE
         Dark shell (distinct from Operations Tracker's light theme)
         with the governance-styling details carried over: the ring
         motif behind the header, the accent border, the per-metric
         top accent bars, and the "DOCUMENT GOVERNANCE" eyebrow label.
         ========================================================== */

      .cvn-admin-doc-shell {
        width: min(1500px, 100%);
        margin: 0 auto;
        background:
          radial-gradient(circle at 12% 0%, rgba(94,212,207,0.06), transparent 32%),
          linear-gradient(160deg, #0B1728, #060F1C 55%, #050C17);
        border: 1px solid rgba(94,212,207,0.20);
        border-radius: 18px;
        overflow: hidden;
        box-shadow: 0 34px 100px rgba(0,0,0,.55);
        min-height: 700px;
      }

      .cvn-admin-doc-header {
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, #0B1426, #10263B);
        border-bottom: 3px solid #19B5B0;
        padding: 34px 38px 32px;
        display: flex;
        justify-content: space-between;
        gap: 24px;
        align-items: flex-start;
      }

      .cvn-admin-doc-header::after {
        content: "";
        position: absolute;
        width: 240px;
        height: 240px;
        right: 90px;
        top: -155px;
        border: 1px solid rgba(25,181,176,.25);
        border-radius: 50%;
        box-shadow:
          0 0 0 28px rgba(25,181,176,.035),
          0 0 0 56px rgba(25,181,176,.025);
        pointer-events: none;
      }

      .cvn-admin-doc-eyebrow {
        position: relative;
        z-index: 1;
        color: #d6ad63;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 1.8px;
        text-transform: uppercase;
        margin-bottom: 7px;
      }

      .cvn-admin-doc-title {
        position: relative;
        z-index: 1;
        font-size: 28px;
        letter-spacing: -.3px;
        line-height: 1.15;
        font-weight: 800;
        margin: 0;
        color: #ffffff !important;
      }

      .cvn-admin-doc-subtitle {
        position: relative;
        z-index: 1;
        max-width: 760px;
        margin-top: 8px;
        color: #aebfd0;
        font-size: 13px;
        line-height: 1.65;
      }

      .cvn-admin-doc-close {
        position: relative;
        z-index: 2;
        border: 1px solid rgba(89,211,206,.35);
        background: rgba(25,181,176,.08);
        color: #f7fafc;
        border-radius: 8px;
        width: 38px;
        height: 38px;
        cursor: pointer;
        font-size: 20px;
      }

      .cvn-admin-doc-close:hover {
        background: rgba(25,181,176,.18);
        border-color: rgba(89,211,206,.6);
      }

      .cvn-admin-doc-content {
        padding: 30px 38px 44px;
      }

      /* Governance indicator bar — a single unified strip with
         per-metric accent lines, rather than four separate cards */
      .cvn-admin-doc-metrics {
        display: grid;
        grid-template-columns: repeat(4,minmax(0,1fr));
        gap: 0;
        margin-bottom: 32px;
        background: linear-gradient(145deg, rgba(15,48,67,0.55), rgba(8,27,43,0.75));
        border: 1px solid rgba(94,212,207,0.20);
        border-radius: 14px;
        overflow: hidden;
      }

      .cvn-admin-metric {
        position: relative;
        background: transparent;
        border: 0;
        border-right: 1px solid rgba(255,255,255,.10);
        border-radius: 0;
        padding: 20px 22px 19px;
      }

      .cvn-admin-metric:last-child {
        border-right: 0;
      }

      .cvn-admin-metric::before {
        content: "";
        position: absolute;
        left: 22px;
        top: 0;
        width: 34px;
        height: 3px;
        background: #19B5B0;
      }

      .cvn-admin-metric-label {
        font-size: 10px;
        font-weight: 800;
        color: #8193a6;
        letter-spacing: 1.25px;
        text-transform: uppercase;
        margin-bottom: 9px;
      }

      .cvn-admin-metric-value {
        font-size: 27px;
        font-weight: 800;
        line-height: 1;
        color: #ffffff;
      }

      .cvn-admin-toolbar {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 15px;
        margin: 0 0 16px;
        padding: 0 0 12px;
        border-bottom: 1px solid rgba(255,255,255,0.08);
      }

      .cvn-admin-register-title {
        font-size: 17px;
        font-weight: 800;
        letter-spacing: -.15px;
        color: #ffffff;
      }

      .cvn-admin-register-title::before {
        content: "DOCUMENT GOVERNANCE";
        display: block;
        color: #59D3CE;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 1.5px;
        margin-bottom: 5px;
      }

      .cvn-admin-refresh {
        border: 1px solid rgba(214,173,99,0.55);
        background: rgba(214,173,99,0.06);
        color: #f0e6d2;
        padding: 9px 15px;
        border-radius: 7px;
        cursor: pointer;
        font-size: 11px;
        font-weight: 800;
        transition: .18s ease;
      }

      .cvn-admin-refresh:hover {
        border-color: #19B5B0;
        color: #59D3CE;
        background: rgba(25,181,176,.06);
      }

      .cvn-admin-table-wrap {
        background: rgba(8,27,43,0.55);
        border: 1px solid rgba(94,212,207,0.18);
        border-radius: 12px;
        overflow-x: auto;
        box-shadow: 0 8px 24px rgba(0,0,0,.14);
      }

      .cvn-admin-table {
        width: 100%;
        border-collapse: collapse;
        min-width: 1100px;
      }

      .cvn-admin-table th {
        text-align: left;
        padding: 13px 14px;
        background: rgba(9,29,46,0.85);
        border-bottom: 2px solid rgba(94,212,207,0.20);
        font-size: 9px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: #8193a6;
      }

      .cvn-admin-table td {
        padding: 15px 14px;
        border-bottom: 1px solid rgba(255,255,255,0.05);
        vertical-align: top;
        font-size: 12px;
        color: #d6e0ea;
      }

      .cvn-admin-table tr:last-child td {
        border-bottom: none;
      }

      .cvn-admin-table tbody tr {
        transition: background .15s ease;
      }

      .cvn-admin-table tbody tr:hover {
        background: rgba(94,212,207,0.05);
      }

      .cvn-admin-code {
        font-weight: 800;
        color: #59D3CE;
        white-space: nowrap;
      }

      .cvn-admin-version {
        font-weight: 800;
        color: #f0e6d2;
      }

      .cvn-admin-status {
        display: inline-flex;
        padding: 5px 8px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 800;
        white-space: nowrap;
      }

      .cvn-admin-status-approved {
        background: rgba(94,212,207,.14);
        color: #7fe6df;
      }

      .cvn-admin-status-draft {
        background: rgba(255,255,255,.08);
        color: #b8c8d8;
      }

      .cvn-admin-status-under_review {
        background: rgba(214,173,99,.16);
        color: #e8c98a;
      }

      .cvn-admin-status-retired,
      .cvn-admin-status-superseded {
        background: rgba(252,165,165,.14);
        color: #fca5a5;
      }

      .cvn-admin-status-default {
        background: rgba(255,255,255,.08);
        color: #b8c8d8;
      }

      .cvn-admin-empty,
      .cvn-admin-error,
      .cvn-admin-loading {
        padding: 40px;
        text-align: center;
        font-size: 13px;
        color: #aebfd0;
      }

      .cvn-admin-error {
        color: #fca5a5;
      }

      @media (max-width: 900px) {
        #cvnAdminDocsOverlay {
          padding: 12px;
        }

        .cvn-admin-doc-header,
        .cvn-admin-doc-content {
          padding-left: 20px;
          padding-right: 20px;
        }

        .cvn-admin-doc-metrics {
          grid-template-columns: repeat(2,minmax(0,1fr));
        }

        .cvn-admin-metric:nth-child(2) {
          border-right: 0;
        }

        .cvn-admin-metric:nth-child(-n+2) {
          border-bottom: 1px solid rgba(255,255,255,.10);
        }

        #cvnAdminDocsLauncher {
          right: 14px;
          bottom: 14px;
        }
      }

      @media (max-width: 560px) {
        .cvn-admin-doc-metrics {
          grid-template-columns: 1fr;
        }

        .cvn-admin-metric {
          border-right: 0;
          border-bottom: 1px solid rgba(255,255,255,.10);
        }

        .cvn-admin-metric:last-child {
          border-bottom: 0;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /* ==========================================================
     UI
     ========================================================== */

  function installAdminUI() {
    if (adminPanelInstalled) return;

    adminPanelInstalled = true;

    installStyles();

    const launcher = document.createElement('button');
    launcher.id = 'cvnAdminDocsLauncher';
    launcher.type = 'button';
    launcher.textContent = 'Admin Documents';
    

    const overlay = document.createElement('div');
    overlay.id = 'cvnAdminDocsOverlay';

    overlay.innerHTML = `
      <div class="cvn-admin-doc-shell">
        <div class="cvn-admin-doc-header">

          <div>
            <div class="cvn-admin-doc-eyebrow">
              Corevian University
            </div>

            <h2 class="cvn-admin-doc-title">
              Controlled Document Command Center
            </h2>

            <div class="cvn-admin-doc-subtitle">
              Administrative document control, version governance,
              assignment readiness and acknowledgment oversight.
            </div>
          </div>

          <button
            class="cvn-admin-doc-close"
            id="cvnAdminDocsClose"
            type="button"
            aria-label="Close"
          >
            ×
          </button>

        </div>

        <div class="cvn-admin-doc-content">

          <div
            id="cvnAdminDocsMetrics"
            class="cvn-admin-doc-metrics"
          ></div>

          <div class="cvn-admin-toolbar">

            <div class="cvn-admin-register-title">
              Controlled Document Register
            </div>

            <button
              type="button"
              id="cvnAdminDocsRefresh"
              class="cvn-admin-refresh"
            >
              Refresh Register
            </button>

          </div>

          <div
            id="cvnAdminDocsRegister"
            class="cvn-admin-table-wrap"
          >
            <div class="cvn-admin-loading">
              Loading controlled-document register…
            </div>
          </div>

        </div>
      </div>
    `;

    // document.body.appendChild(launcher);
    document.body.appendChild(overlay);

    launcher.addEventListener('click', async function () {
      overlay.classList.add('cvn-open');
      document.body.style.overflow = 'hidden';
      await refreshAdminRegister();
    });

    document
      .getElementById('cvnAdminDocsClose')
      .addEventListener('click', closeAdminPanel);

    document
      .getElementById('cvnAdminDocsRefresh')
      .addEventListener('click', refreshAdminRegister);

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) {
        closeAdminPanel();
      }
    });
  }

  function closeAdminPanel() {
    const overlay = document.getElementById('cvnAdminDocsOverlay');

    if (overlay) {
      overlay.classList.remove('cvn-open');
    }

    document.body.style.overflow = '';
  }

  /* ==========================================================
     REGISTER RENDERING
     ========================================================== */

  function renderMetrics(rows) {
    const target = document.getElementById('cvnAdminDocsMetrics');

    if (!target) return;

    const uniqueDocuments = new Set(
      rows.map(row => row.document_id)
    ).size;

    const approved = rows.filter(
      row => row.version_status === 'approved'
    ).length;

    const underReview = rows.filter(
      row => row.version_status === 'under_review'
    ).length;

    const outstandingAssignments = rows.reduce(
      (total, row) =>
        total + Number(row.outstanding_assignment_count || 0),
      0
    );

    target.innerHTML = `
      <div class="cvn-admin-metric">
        <div class="cvn-admin-metric-label">
          Controlled Documents
        </div>
        <div class="cvn-admin-metric-value">
          ${uniqueDocuments}
        </div>
      </div>

      <div class="cvn-admin-metric">
        <div class="cvn-admin-metric-label">
          Approved Versions
        </div>
        <div class="cvn-admin-metric-value">
          ${approved}
        </div>
      </div>

      <div class="cvn-admin-metric">
        <div class="cvn-admin-metric-label">
          Under Review
        </div>
        <div class="cvn-admin-metric-value">
          ${underReview}
        </div>
      </div>

      <div class="cvn-admin-metric">
        <div class="cvn-admin-metric-label">
          Outstanding Assignments
        </div>
        <div class="cvn-admin-metric-value">
          ${outstandingAssignments}
        </div>
      </div>
    `;
  }

  function renderRegister(rows) {
    const target = document.getElementById('cvnAdminDocsRegister');

    if (!target) return;

    if (!Array.isArray(rows) || !rows.length) {
      target.innerHTML = `
        <div class="cvn-admin-empty">
          No controlled documents are currently available.
        </div>
      `;
      return;
    }

    const body = rows.map(row => `
      <tr>
        <td>
          <div class="cvn-admin-code">
            ${escapeHTML(row.document_code)}
          </div>
          <div style="margin-top:4px;color:#8193a6;">
            ${escapeHTML(row.title)}
          </div>
        </td>

        <td>
          ${escapeHTML(row.document_type || '—')}
        </td>

        <td>
          ${escapeHTML(row.department || '—')}
        </td>

        <td class="cvn-admin-version">
          ${escapeHTML(row.version_number)}
        </td>

        <td>
          <span class="cvn-admin-status ${statusClass(row.version_status)}">
            ${escapeHTML(statusLabel(row.version_status))}
          </span>
        </td>

        <td>
          ${formatDate(row.effective_date)}
        </td>

        <td>
          ${formatDate(row.review_due_date)}
        </td>

        <td>
          ${Number(row.assignment_count || 0)}
        </td>

        <td>
          ${Number(row.completed_assignment_count || 0)}
        </td>

        <td>
          ${Number(row.outstanding_assignment_count || 0)}
        </td>

        <td>
          ${Number(row.acknowledgment_count || 0)}
        </td>

        <td>
          ${escapeHTML(row.approved_by_name || '—')}
        </td>
      </tr>
    `).join('');

    target.innerHTML = `
      <table class="cvn-admin-table">

        <thead>
          <tr>
            <th>Document</th>
            <th>Type</th>
            <th>Department</th>
            <th>Version</th>
            <th>Status</th>
            <th>Effective</th>
            <th>Review Due</th>
            <th>Assigned</th>
            <th>Completed</th>
            <th>Outstanding</th>
            <th>Acknowledgments</th>
            <th>Approved By</th>
          </tr>
        </thead>

        <tbody>
          ${body}
        </tbody>

      </table>
    `;
  }

  async function refreshAdminRegister() {
    const target = document.getElementById('cvnAdminDocsRegister');

    if (target) {
      target.innerHTML = `
        <div class="cvn-admin-loading">
          Loading controlled-document register…
        </div>
      `;
    }

    try {
      adminRegister = await loadAdminRegister();

      renderMetrics(adminRegister);
      renderRegister(adminRegister);

    } catch (error) {
      console.error(
        'Corevian Admin Document Register error:',
        error
      );

      if (target) {
        target.innerHTML = `
          <div class="cvn-admin-error">
            ${escapeHTML(error.message)}
          </div>
        `;
      }
    }
  }

  /* ==========================================================
     ADMIN ACCESS CHECK
     ========================================================== */

  async function initializeAdminDocuments() {
    if (isDemoMode()) return;

    const session = await getAuthSession();

    if (!session || !session.access_token) return;

    try {
      /*
        We intentionally use the protected Admin Register RPC
        as the access check.

        Active admins receive the register.
        Other roles are rejected by the backend and never
        receive the Admin Documents interface.
      */

      adminRegister = await loadAdminRegister();

      if (!Array.isArray(adminRegister)) return;

      installAdminUI();

    } catch (error) {
      /*
        Expected behavior for Employee / Trainer / Leadership.
        Do not expose an admin launcher.
      */

      console.info(
        'Corevian Admin Documents: administrative access not available.'
      );
    }
  }

  /* ==========================================================
     PUBLIC OPEN FUNCTION
     Exposes a real, callable entry point for the Quick Access
     dashboard card and for corevian-university-executive-operations.js,
     which already looks for exactly this function name.
     ========================================================== */

  async function openAdminDocumentsPanel() {
    let overlay = document.getElementById('cvnAdminDocsOverlay');

    if (overlay) {
      overlay.classList.add('cvn-open');
      document.body.style.overflow = 'hidden';
      await refreshAdminRegister();
      return true;
    }

    try {
      adminRegister = await loadAdminRegister();

      if (!Array.isArray(adminRegister)) return false;

      installAdminUI();
      overlay = document.getElementById('cvnAdminDocsOverlay');

      if (overlay) {
        overlay.classList.add('cvn-open');
        document.body.style.overflow = 'hidden';
        await refreshAdminRegister();
        return true;
      }

      return false;

    } catch (error) {
      console.info(
        'Corevian Admin Documents: administrative access not available.'
      );
      return false;
    }
  }

  window.openCorevianAdminDocuments = openAdminDocumentsPanel;

  /* ==========================================================
     START
     ========================================================== */

  function start() {
    setTimeout(initializeAdminDocuments, 700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

})();